<!--@format-->
<template>
    <div class="add-bank-account-widget" :class="componentClasses">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ isLoading ? 'Loading...' : 'Add Bank Account' }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else class="widget-container">
                    <div class="payment edit">
                        <div class="form">
                            <div class="bank-account-name-wrapper">
                                <app-input
                                    v-model="name"
                                    label="Bank Account Nickname"
                                    placeholder="To distinguish this account from others, give it a distinctive name"
                                    required
                                    :is-valid="getIsValidField('name')"
                                    :class="{ 'has-errors': !getIsValidField('name') }"
                                    error="Required"
                                />
                            </div>
                            <app-input
                                v-model="businessName"
                                label="Business Name"
                                placeholder=" "
                                required
                                :is-valid="getIsValidField('businessName')"
                                :class="{ 'has-errors': !getIsValidField('businessName') }"
                                error="Required"
                            />
                            <div class="bank-account-graphic-wrapper">
                                <img
                                    src="@/assets/images/SVG/check-graphic.svg"
                                    alt="bank account details graph"
                                />
                            </div>
                            <app-dropdown
                                attach
                                label="Account Holder Type"
                                :items="accountHolderTypeOptions"
                                :is-valid="getIsValidField('selectedAccountHolderType')"
                                :class="{
                                    'has-errors': !getIsValidField('selectedAccountHolderType'),
                                }"
                                placeholder="Select Account Holder Type"
                                :value="selectedAccountHolderType"
                                error="Required"
                                @input="updateSelectedAccountHolderType"
                            />
                            <app-input
                                v-model="routingNumber"
                                label="Routing Number (9 digits)"
                                placeholder=" "
                                type="number"
                                required
                                :is-valid="getIsValidField('routingNumber')"
                                :class="{ 'has-errors': !getIsValidField('routingNumber') }"
                                error="Required"
                            />
                            <app-input
                                v-model="routingNumberConfirmation"
                                label="Confirm Routing Number"
                                placeholder=" "
                                type="number"
                                required
                                :is-valid="getIsValidField('routingNumberConfirmation')"
                                :class="{
                                    'has-errors': !getIsValidField('routingNumberConfirmation'),
                                }"
                                error="Required"
                            />
                            <app-input
                                v-model="accountNumber"
                                label="Account Number (4-17 digits)"
                                placeholder=" "
                                type="number"
                                required
                                :is-valid="getIsValidField('accountNumber')"
                                :class="{ 'has-errors': !getIsValidField('accountNumber') }"
                                error="Required"
                            />
                            <app-input
                                v-model="accountNumberConfirmation"
                                label="Confirm Account Number"
                                placeholder=" "
                                type="number"
                                required
                                :is-valid="getIsValidField('accountNumberConfirmation')"
                                :class="{
                                    'has-errors': !getIsValidField('accountNumberConfirmation'),
                                }"
                                error="Required"
                            />
                            <div class="form-conditions">
                                <div>
                                    By clicking save, you authorize GlassHive to debit the bank
                                    account specified for any amount owed for charges arising from
                                    your use of GlassHive's services and/or purchase of products
                                    from GlassHive, pursuant to GlassHive's website and terms, until
                                    this authorization is revoked. You may cancel this authorization
                                    at any time by removing this payment method.
                                </div>
                                <div>
                                    If you use GlassHive's services or purchase additional products
                                    periodically pursuant to GlassHive's terms, you authorize
                                    GlassHive to debit your bank account periodically. Payments that
                                    fall outside of the regular debits authorized will only be
                                    debited after your authorization is obtained.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" :disabled="processing" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <app-button class="save-btn" :loading="processing" @click="saveData">
                    Save
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const AppInput = () => import('@/components/common/app-input');
const AppDropdown = () => import('@/components/common/app-dropdown');
import { responsiveHelpers } from '@/scripts/helpers';
import { PaymentMethodsService } from '@/scripts/services/payment-methods/payment-methods-service';
import EventBus from '@/scripts/event-bus.js';
import { ResponsiveMixin } from 'vue-responsive-components';
import { maxLength, minLength, numeric, required, sameAs } from 'vuelidate/lib/validators';
import { LogLevel } from '@/scripts/logger';

export default {
    name: 'add-bank-account-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'app-input': AppInput,
        'app-dropdown': AppDropdown,
    },

    mixins: [ResponsiveMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            processing: false,
            clientId: this.widget.clientId,
            agencyId: this.widget.agencyId,
            name: '',
            businessName: '',
            selectedAccountHolderType: null,
            routingNumber: '',
            routingNumberConfirmation: '',
            accountNumber: '',
            accountNumberConfirmation: '',

            accountHolderTypeOptions: [
                {
                    id: 1,
                    name: 'Company',
                    value: 'company',
                },
                {
                    id: 2,
                    name: 'Individual',
                    value: 'individual',
                },
            ],
        };
    },

    computed: {
        hasAgencyBillingPaymentPermission() {
            return (
                this.$store.getters.isBillingPaymentUser ||
                this.$store.getters.isOrganizationUser ||
                this.$store.getters.isMasterAgencyUser ||
                this.$store.getters.isAgencyAdmin
            );
        },

        isAgencyContext() {
            return this.$store.state.context.agency.id;
        },

        componentClasses() {
            return [responsiveHelpers.getBreakpointClasses(this)];
        },

        userConfig() {
            if (this.clientId) {
                return { userType: 'client', id: this.clientId };
            } else if (this.agencyId) {
                return { userType: 'agency', id: this.agencyId };
            } else {
                return { userType: 'masterAgency', id: this.agencyId };
            }
        },
    },

    breakpoints: responsiveHelpers.getBreakpoints(),

    validations() {
        return {
            name: {
                required,
            },

            businessName: {
                required,
            },

            selectedAccountHolderType: {
                required,
            },

            routingNumber: {
                required,
                numeric,
                minLength: minLength(9),
                maxLength: maxLength(9),
            },

            routingNumberConfirmation: {
                required,
                numeric,
                minLength: minLength(9),
                maxLength: maxLength(9),
                sameAs: sameAs(() => this.routingNumber),
            },

            accountNumber: {
                required,
                numeric,
                minLength: minLength(4),
                maxLength: maxLength(17),
            },

            accountNumberConfirmation: {
                required,
                numeric,
                minLength: minLength(4),
                maxLength: maxLength(17),
                sameAs: sameAs(() => this.accountNumber),
            },
        };
    },

    created() {
        this.validateRoles();
    },

    methods: {
        validateRoles() {
            if (this.isAgencyContext && !this.hasAgencyBillingPaymentPermission) {
                this.closeWidget();
                const errorMessage =
                    'You need additional permissions in order to perform this action.';
                this.$helpers.showFailNotification(errorMessage, { level: LogLevel.INFO });
            }
        },

        getIsValidField(field) {
            const isInvalidValid = this.$v[field].$invalid && this.$v[field].$dirty;
            return !isInvalidValid;
        },

        updateSelectedAccountHolderType(accountHolderType) {
            this.selectedAccountHolderType = accountHolderType;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        saveData() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }

            const request = {
                NickName: this.name,
                Name: this.businessName,
                Country: 'US',
                Currency: 'USD',
                AccountHolderType: this.selectedAccountHolderType.value,
                RoutingNumber: this.routingNumber,
                AccountNumber: this.accountNumber,
                PaymentType: 'bank_account',
            };
            this.processing = true;
            PaymentMethodsService.create(request, this.userConfig.id, this.userConfig.userType)
                .then(() => {
                    this.$helpers.showSuccessNotification('Billing saved successfully.');
                    EventBus.$emit('payment-method:update', true);
                    EventBus.$emit('add-bank-account-widget-to-user-widget', this.widget);
                    EventBus.$emit('add-bank-account-widget-to-cw-invite-user', this.widget);
                    EventBus.$emit(
                        'add-bank-account-widget-to-third-party-invite-user',
                        this.widget,
                    );
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while updating your billing.',
                        { error },
                    );
                })
                .finally(() => {
                    this.processing = false;
                });
        },
    },
};
</script>

<style>
.add-bank-account-widget {
    color: #382153;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
input[type='number'] {
    appearance: textfield;
}

.add-bank-account-widget .save-btn {
    width: 128px;
    height: 40px;
}

.add-bank-account-widget .bank-account-details-wrapper {
    display: flex;
    gap: 20px;
}

.add-bank-account-widget .bank-account-details-wrapper > * {
    width: 100%;
}

.add-bank-account-widget .form > *:not(:first-child) {
    margin-top: 20px;
}

.add-bank-account-widget .form-conditions {
    font: normal normal normal 14px Roboto;
    color: #877d88;
}

.add-bank-account-widget label {
    display: block;
    font: 500 16px/1em Roboto;
    margin-bottom: 7px;
    color: #382153;
}

.add-bank-account-widget .form-conditions > *:not(:first-child) {
    margin-top: 20px;
}

.add-bank-account-widget .cancel-btn {
    background: transparent;
    border: 1px solid #cac6cb;
    font: normal normal medium 14px/24px Roboto;
    color: #877d88;
    min-width: 128px;
    min-height: 40px;
}

.add-bank-account-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.add-bank-account-widget .bank-account-name-wrapper {
    padding-bottom: 20px;
    border-bottom: 2px solid #e9e9ef;
}

.add-bank-account-widget label {
    display: block;
    font: 500 16px/1em Roboto;
    margin-bottom: 7px;
    color: #382153;
}

.add-bank-account-widget .bank-account-graphic-wrapper {
    display: flex;
    justify-content: center;
}

.add-bank-account-widget .app-input.error.has-errors input,
.add-bank-account-widget .gray-container.has-errors {
    border: 2px solid #f30070;
}

.add-bank-account-widget .title-label.has-errors {
    color: #f30070;
}

.add-bank-account-widget .app-input-radio {
    display: flex;
    flex-wrap: wrap;
    justify-content: left;
    padding: 0;
}

.add-bank-account-widget .app-input-radio .option {
    margin: 25px 30px 12px 0;
}

.add-bank-account-widget .app-input-radio .radio-option-label {
    color: #382153;
}

.add-bank-account-widget .app-input.error label,
.add-bank-account-widget .app-dropdown.error label {
    color: #382153;
}
</style>
